<template>
  <v-container>
    <v-form ref="terms-of-delivery-create-form">
      <create-terms-of-delivery-dialog
        :terms-of-delivery="termsOfDelivery"
        :country-of-delivery="geographicalCountries"
        :destination-logistics-country="geographicalCountries"
        :mode-of-transports="activeModeOfTransports"
        :mode-of-transport-category="activeModeOfTransportCategories"
        :packing-mode="packingModes"
        :inco-term="activeIncoTerms"
        :validation-error-messages="validationErrorMessages"
        :rules="rules"
        :timelineRequired="true"
        @save="save"
        @cancel="cancel"/>
    </v-form>

    <base-dialog-send
      :send-dialog="sendDialog"
      :show-copy=false
      label="Terms of delivery has been saved successfully!"
      @create="createNew"
      @search="search"/>

    <v-dialog
      v-model="noLltCoverageWarningDialog"
      max-width="780"
      persistent>
      <v-card>
        <v-card-title class="justify-center font-weight-bold pt-6">
          <v-icon left color="yellow darken-3">mdi-alert</v-icon>
          No active Logistic Lead Time found
        </v-card-title>
        <v-card-text class="pt-4 text-body-1">
          The Terms of Delivery was saved successfully.<br><br>
          However, no active, published Logistic Lead Time was found matching the selected
          <strong>country of delivery</strong>, <strong>mode of transport</strong>
          and <strong>destination logistics country</strong> combination.
          <br><br>
          You may want to create or review the relevant Logistic Lead Time(s) before this
          Terms of Delivery becomes effective.
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <base-input-primary-button
            icon="mdi-check-decagram"
            label="Ok"
            @click="acknowledgeWarning"/>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import BaseDialogSend from '@/base/dialog/BaseDialogSend'
import BaseInputPrimaryButton from '@/base/input/BaseInputPrimaryButton'
import CreateTermsOfDeliveryDialog
  from '@/components/termsofdelivery/createtermsofdelivery/CreateTermsOfDeliveryDialog.vue'
import { validateDate } from '@/validation/customvalidation'
import { mapGetters } from 'vuex'

export default {
  name: 'CreateTermsOfDelivery',
  components: { CreateTermsOfDeliveryDialog, BaseDialogSend, BaseInputPrimaryButton },
  data () {
    return {
      sendDialog: false,
      noLltCoverageWarningDialog: false,
      validationErrorMessages: [],
      termsOfDelivery: {
        countryOfDeliveryId: '',
        destinationLogisticCountryIds: [],
        modeOfTransportId: '',
        modeOfTransportCategoryId: '',
        packingModeId: '',
        incoTermId: '',
        hazMat: '',
        validFrom: '',
        validTo: '',
        publishTime: ''
      },
      rules: {
        required: value => !!value || 'Required.',
        selected: value => value.length > 0 || 'Select one of the item.',
        fromDateIsBeforeToDate: value => validateDate(this.termsOfDelivery.validFrom, value) || 'Valid to date cannot be before valid from date.'
      }
    }
  },
  computed: {
    ...mapGetters([
      'getUserRoles',
      'geographicalCountries',
      'packingModes',
      'activeIncoTerms',
      'activeModeOfTransports',
      'activeModeOfTransportCategories',
      'freightServices',
      'planningMarkets'
    ])
  },
  async mounted () {
    await axios.all([
      this.$store.dispatch('getAllGeographicalCountries'),
      this.$store.dispatch('getAllIncoTerms'),
      this.$store.dispatch('getAllPackingModes'),
      this.$store.dispatch('getAllModeOfTransports'),
      this.$store.dispatch('getAllModeOfTransportCategories'),
      this.$store.dispatch('getAllFreightServices'),
      this.$store.dispatch('getAllPlanningMarkets')
    ]).then(() => {
      this.isLoaded = true
      this.applyRouteParamCopyData()
    })
  },
  methods: {
    applyRouteParamCopyData () {
      const copyData = this.$route.params.copyData
      if (!copyData) {
        return
      }
      this.termsOfDelivery.countryOfDeliveryId = copyData.countryOfDeliveryId
      this.termsOfDelivery.modeOfTransportId = copyData.modeOfTransportId
      this.termsOfDelivery.modeOfTransportCategoryId = copyData.modeOfTransportCategoryId
      this.termsOfDelivery.packingModeId = copyData.packingModeId
      this.termsOfDelivery.incoTermId = copyData.incoTermId
      this.termsOfDelivery.hazMat = copyData.hazMat
    },
    validate () {
      return this.$refs['terms-of-delivery-create-form'].validate()
    },
    save () {
      if (this.validate()) {
        axios.post('/api/touchpoint/terms-of-delivery/create', this.termsOfDelivery)
          .then((response) => {
            if (response.data.status === 'SUCCESS') {
              this.checkLltCoverage()
            } else {
              this.validationErrorMessages = response.data.errorMessageList
              window.scrollTo(0, 0)
            }
          })
          .catch(e => console.log(e))
      } else {
        window.scrollTo(0, 0)
      }
    },
    async checkLltCoverage () {
      try {
        const mot = this.activeModeOfTransports.find(m => m.id === this.termsOfDelivery.modeOfTransportId)
        const motExternalId = mot ? mot.externalId : null

        const freightServiceIds = motExternalId !== null
          ? this.freightServices.filter(fs => fs.sdsId === motExternalId).map(fs => fs.id)
          : []

        // A MoT was selected but maps to no freight service → no LLT can match.
        if (motExternalId !== null && freightServiceIds.length === 0) {
          this.noLltCoverageWarningDialog = true
          return
        }

        const planningMarketIds = this.planningMarkets
          .filter(pm => pm.logisticCountry &&
            this.termsOfDelivery.destinationLogisticCountryIds.includes(pm.logisticCountry.id))
          .map(pm => pm.id)

        // Destination countries were selected but map to no planning markets → no LLT can match.
        if (this.termsOfDelivery.destinationLogisticCountryIds.length > 0 && planningMarketIds.length === 0) {
          this.noLltCoverageWarningDialog = true
          return
        }

        const searchCriteria = {
          countryOfDeliveryIds: [this.termsOfDelivery.countryOfDeliveryId],
          freightServiceIds,
          planningMarketIds,
          published: true,
          active: true
        }

        const response = await axios.post('/api/touchpoint/logistic-lead-time/criteria-search', {
          searchCriteria,
          page: 1
        })

        if (response.data.count === 0) {
          this.noLltCoverageWarningDialog = true
        } else {
          this.sendDialog = true
        }
      } catch (e) {
        console.error('Failed to check LLT coverage', e)
        this.sendDialog = true
      }
    },
    acknowledgeWarning () {
      this.noLltCoverageWarningDialog = false
      this.sendDialog = true
    },
    cancel () {
      this.$router.push({ name: 'SearchTermsOfDelivery' })
    },
    createNew () {
      this.sendDialog = false
      this.termsOfDelivery = {
        countryOfDeliveryId: '',
        destinationLogisticCountryIds: [],
        modeOfTransportId: '',
        modeOfTransportCategoryId: '',
        packingModeId: '',
        incoTermId: '',
        hazMat: '',
        validFrom: '',
        validTo: '',
        publishTime: ''
      }
      this.$refs['terms-of-delivery-create-form'].resetValidation()
    },
    search () {
      this.$router.push({ name: 'SearchTermsOfDelivery' })
    }
  }
}
</script>

<style scoped>
</style>
